import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useOwnerCalendar } from '../../context/OwnerCalendarContext';
import { getServiceDuration } from '../../utils/calendar/durationService';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const useOwnerBookingLogic = ({
                                         trainings = [],
                                         trainingLocks = [],
                                         confirmedTrainings = [],
                                         slotBookings = [],
                                         workingHours = [],
                                         selectedService = null
                                     }) => {
    const {
        selectedDogId,
        schoolSettings,
        canBookSlot,
        canCancelTraining,
    } = useOwnerCalendar();

    const serviceDurationMinutes = selectedService ? getServiceDuration(selectedService) : 60;

    const dayOfWeekMapping = useMemo(() => ({
        1: 'MONDAY',
        2: 'TUESDAY',
        3: 'WEDNESDAY',
        4: 'THURSDAY',
        5: 'FRIDAY',
        6: 'SATURDAY',
        7: 'SUNDAY'
    }), []);

    const isSlotBookedByOwner = useCallback((slotTime) => {
        const slot = dayjs(slotTime);
        return trainings.some(training =>
            dayjs(training.startTime).isSame(slot, 'minute')
        );
    }, [trainings]);

    const isSlotLocked = useCallback((slotTime, duration = serviceDurationMinutes) => {
        const slot = dayjs(slotTime);
        const slotEnd = slot.add(duration, 'minute');

        return trainingLocks.some(lock => {
            const lockStart = dayjs(lock.startTime);
            const lockEnd = dayjs(lock.endTime);
            return (slot.isBefore(lockEnd) && slotEnd.isAfter(lockStart));
        });
    }, [trainingLocks, serviceDurationMinutes]);

    const isSlotConfirmedByOthers = useCallback((slotTime, duration = serviceDurationMinutes) => {
        const slot = dayjs(slotTime);
        const slotEnd = slot.add(duration, 'minute');

        return confirmedTrainings.some(ct => {
            const ctStart = dayjs(ct.startTime);
            const ctEnd = dayjs(ct.endTime);
            return (slot.isBefore(ctEnd) && slotEnd.isAfter(ctStart));
        });
    }, [confirmedTrainings, serviceDurationMinutes]);

    const getSlotBookingInfo = useCallback((slotTime) => {
        const slot = dayjs(slotTime);
        return slotBookings.find(b =>
            dayjs(b.slot).isSame(slot, 'minute')
        );
    }, [slotBookings]);

    const getSlotStatus = useCallback((slotTime) => {
        const slot = dayjs(slotTime);
        const now = dayjs();

        const ownerBooking = trainings.find(t =>
            dayjs(t.startTime).isSame(slot, 'minute')
        );
        if (ownerBooking) {
            return {
                type: ownerBooking.confirmed ? 'owner-confirmed' : 'owner-pending',
                booking: ownerBooking,
                canCancel: canCancelTraining(ownerBooking)
            };
        }

        if (isSlotLocked(slot, serviceDurationMinutes)) {
            return { type: 'locked', canBook: false };
        }

        if (isSlotConfirmedByOthers(slot, serviceDurationMinutes)) {
            return { type: 'confirmed-other', canBook: false };
        }

        if (slot.isBefore(now)) {
            return { type: 'past', canBook: false };
        }

        if (!canBookSlot(slot)) {
            return { type: 'unavailable', canBook: false };
        }

        const bookingInfo = getSlotBookingInfo(slot);
        return {
            type: 'free',
            canBook: true,
            bookingCount: bookingInfo?.bookingCount || 0
        };
    }, [trainings, isSlotLocked, isSlotConfirmedByOthers, canBookSlot, canCancelTraining, getSlotBookingInfo, serviceDurationMinutes]);

    const getTimeSlotsForDay = useCallback((dayDate, duration = serviceDurationMinutes) => {
        const dayKey = dayOfWeekMapping[dayDate.isoWeekday()];
        const dayWorkingHours = workingHours.find(h => h.dayOfWeek === dayKey);

        if (!dayWorkingHours) return [];

        const slots = [];
        const [openHour, openMinute] = dayWorkingHours.openingTime.split(':').map(Number);
        const [closeHour, closeMinute] = dayWorkingHours.closingTime.split(':').map(Number);

        let slotTime = dayDate.hour(openHour).minute(openMinute).second(0);
        const endTime = dayDate.hour(closeHour).minute(closeMinute).second(0);

        while (slotTime.add(duration, 'minute').isSameOrBefore(endTime)) {
            slots.push({
                time: slotTime.clone(),
                status: getSlotStatus(slotTime)
            });
            slotTime = slotTime.add(duration, 'minute');
        }

        return slots;
    }, [workingHours, dayOfWeekMapping, getSlotStatus, serviceDurationMinutes]);

    const getFreeSlotsForDay = useCallback((dayDate, duration = serviceDurationMinutes) => {
        const allSlots = getTimeSlotsForDay(dayDate, duration);
        return allSlots
            .filter(slot => slot.status.type === 'free' && slot.status.canBook)
            .map(slot => slot.time.format('YYYY-MM-DDTHH:mm:ss'));
    }, [getTimeSlotsForDay, serviceDurationMinutes]);

    const isMonthBlocked = useCallback((date) => {
        if (!schoolSettings?.unlockedMonths || schoolSettings.unlockedMonths.length === 0) {
            return false;
        }

        const monthNames = [
            'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
            'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
        ];
        const dateMonth = monthNames[dayjs(date).month()];

        return !schoolSettings.unlockedMonths.includes(dateMonth);
    }, [schoolSettings]);

    const getWorkingHoursForDay = useCallback((dayDate) => {
        const dayKey = dayOfWeekMapping[dayDate.isoWeekday()];
        return workingHours.find(h => h.dayOfWeek === dayKey);
    }, [workingHours, dayOfWeekMapping]);

    const hasBookableSlotsInWeek = useCallback((weekStart) => {
        for (let i = 0; i < 7; i++) {
            const dayDate = weekStart.add(i, 'day');
            const freeSlots = getFreeSlotsForDay(dayDate);
            if (freeSlots.length > 0) return true;
        }
        return false;
    }, [getFreeSlotsForDay]);

    const validateMultipleBooking = useCallback((selectedSlots) => {
        if (!selectedSlots || selectedSlots.length === 0) {
            return { valid: false, error: 'Nie wybrano żadnych terminów' };
        }

        if (!selectedDogId) {
            return { valid: false, error: 'Nie wybrano pupila' };
        }

        const unavailableSlots = selectedSlots.filter(slotStr => {
            const slot = dayjs(slotStr, 'YYYY-MM-DDTHH:mm:ss');
            const status = getSlotStatus(slot);
            return status.type !== 'free' || !status.canBook;
        });

        if (unavailableSlots.length > 0) {
            return {
                valid: false,
                error: `${unavailableSlots.length} z wybranych terminów nie jest już dostępnych`
            };
        }

        return { valid: true };
    }, [selectedDogId, getSlotStatus]);

    const getSelectedSlotsSummary = useCallback((selectedSlots) => {
        if (!selectedSlots || selectedSlots.length === 0) {
            return { count: 0, dates: [] };
        }

        const dateGroups = {};
        selectedSlots.forEach(slotStr => {
            const slot = dayjs(slotStr, 'YYYY-MM-DDTHH:mm:ss');
            const dateKey = slot.format('YYYY-MM-DD');
            if (!dateGroups[dateKey]) {
                dateGroups[dateKey] = [];
            }
            dateGroups[dateKey].push(slot.format('HH:mm'));
        });

        const dates = Object.keys(dateGroups).sort().map(dateKey => ({
            date: dayjs(dateKey),
            times: dateGroups[dateKey].sort()
        }));

        return {
            count: selectedSlots.length,
            dates
        };
    }, []);

    return {
        isSlotBookedByOwner,
        isSlotLocked,
        isSlotConfirmedByOthers,
        getSlotStatus,
        getSlotBookingInfo,
        getTimeSlotsForDay,
        getFreeSlotsForDay,
        getWorkingHoursForDay,
        isMonthBlocked,
        hasBookableSlotsInWeek,
        validateMultipleBooking,
        getSelectedSlotsSummary,
        dayOfWeekMapping,
        serviceDurationMinutes,
    };
};